/* eslint-disable @1js/office-online-rules/no-v8-imports-for-fluentui */
/* --reason: This rule is disabled to accommodate existing v8 styles usage.
When they have been completely deprecated, ensureMinimumTargetSizesLegacy can be removed. */
import {
  concatStyleSets,
  type IButtonStyles,
  type IRawStyle,
} from "@fluentui/react";
import { controlStylingConstants } from "./controlStylingConstants";

/**
 * Update the width and minWidth properties of legacy v8 button styles to ensure that
 * they are at least minimum target size (24px) for web a11y compliance.
 *
 * If no width/minWidth is specified, they will be set to the minimum target size.
 * If the width/minWidth already exceeds the 24px requirement, the original styles will be preserved.
 *
 * This method should be called after all other base and custom styles have been combined.
 */
export const ensureMinimumTargetSizesLegacy = (
  styles: IButtonStyles,
  isMinTargetSizesEnabled: boolean
): IButtonStyles => {
  if (isMinTargetSizesEnabled) {
    if (hasRootArray(styles)) {
      return ensureMinTargetSizesForConcatStyles(styles);
    } else if (hasRootStyle(styles)) {
      return ensureMinTargetSizesForMergedStyles(styles);
    }
  }
  return styles;
};

/**
 * Used for merged IButtonStyles where root is a single IRawStyle object
 * If a compliant width is needed, it is merged into the root object.
 */
const ensureMinTargetSizesForMergedStyles = (
  styles: IButtonStyles
): IButtonStyles => {
  const root = styles.root as IRawStyle;
  const originalWidth = parseWidth(root.width);
  const originalMinWidth = parseWidth(root.minWidth);

  // Return original styles if they're already compliant:
  // 1) width is sufficient
  // 2) width is not sufficient (undefined, auto, or undersized) but minWidth is sufficient
  if (
    originalWidth >= controlStylingConstants.minTargetSize ||
    originalMinWidth >= controlStylingConstants.minTargetSize
  ) {
    return styles;
  }

  // Otherwise build the new style with compliant widths
  // if width was originally "auto" or undefined, preserve it
  return {
    ...styles,
    root: {
      ...root,
      width:
        originalWidth === -1
          ? root.width
          : controlStylingConstants.minTargetSize,
      minWidth: controlStylingConstants.minTargetSize,
    },
  };
};

/**
 * Used for concatenated IButtonStyles where root is an IRawStyle array.
 * If an additional compliant style is needed, it is appended to the end of the array.
 */
const ensureMinTargetSizesForConcatStyles = (styles: IButtonStyles) => {
  const root = styles.root as IRawStyle[];
  let lastWidth: string | number | undefined = undefined;
  let lastMinWidth: string | number | undefined = undefined;

  root.forEach((style, _index) => {
    lastWidth = (style as IRawStyle).width ?? lastWidth;
    lastMinWidth = (style as IRawStyle).minWidth ?? lastMinWidth;
  });

  const originalWidth = parseWidth(lastWidth);
  const originalMinWidth = parseWidth(lastMinWidth);

  if (
    originalWidth >= controlStylingConstants.minTargetSize ||
    originalMinWidth >= controlStylingConstants.minTargetSize
  ) {
    return styles;
  }

  return concatStyleSets(styles, {
    root: {
      width:
        originalWidth === -1
          ? undefined
          : controlStylingConstants.minTargetSize,
      minWidth: controlStylingConstants.minTargetSize,
    },
  });
};

const hasRootStyle = (style: any) => {
  return !!style.root && typeof style.root === "object";
};

const hasRootArray = (styles: any) => {
  return !!styles.root && Array.isArray(styles.root);
};

/**
 * Parses the numeric value from an IStyle object's width property.
 * If no number can be parsed, -1 is returned.
 */
const parseWidth = (width: string | number | undefined) => {
  if (typeof width === "number") {
    return width;
  }
  if (typeof width === "string") {
    const parsed = parseInt(width, 10);
    if (!isNaN(parsed)) {
      return parsed;
    }
  }

  return -1;
};
